/**
 * MFX OS — PO review screen (blueprint Stage 2-3). Three panes: original PO
 * (file link + extracted fields), comparison verdicts against the approved
 * quote, and the controlled action rail. Approval is hard-gated by blockers;
 * "Approve With Authorized Exception" records reason/category/impact/risk.
 * "Approve PO and Create Sales Order" is ONE controlled command.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOsDoc, getOsDoc, usd, fmtDate } from '../../os/core';
import {
  approveCustomerPO, createSalesOrderFromPO, matchPOToQuote,
  rejectCustomerPO, requestClientClarification, resolveClientClarification,
} from '../../os/commands';
import { type QuoteDoc } from '../../os/compare';
import { Blockers, Chip, Lineage, Modal, Timeline } from '../../os/ui';
import type { CustomerPO, POException } from '../../os/types';
import { statusColor } from '../../os/types';
import { loadRecentQuotes, type RecentQuote } from '../../lib/osData';

export default function PoReview() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const id = sp.get('id') || '';
  const { data: po, loading } = useOsDoc<CustomerPO>('customerPOs', id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [exOpen, setExOpen] = useState(false);
  const [clarOpen, setClarOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [tab, setTab] = useState<'compare' | 'po' | 'activity'>('compare');

  const run = async (fn: () => Promise<unknown>, after?: (r: unknown) => void) => {
    setBusy(true); setErr('');
    try { const r = await fn(); after?.(r); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /> Loading PO…</div>;
  if (!po) return <div className="empty">PO not found. <button className="btn sm" onClick={() => nav('/os/pos')}>Back to inbox</button></div>;

  const cmp = po.comparison;
  const blockers = cmp?.blockers || ['Comparison has not run yet — link a quote.'];
  const canApprove = po.status === 'REVIEW_REQUIRED' || po.status === 'RECEIVED' || po.status === 'EXCEPTION';
  const approved = po.status === 'APPROVED';

  return (
    <>
      <Lineage parts={[
        { label: po.clientName || 'Client' },
        ...(po.quoteNum ? [{ label: `Quote ${po.quoteNum}` }] : []),
        { label: `PO ${po.poNumber}` },
        ...(po.salesOrderNum ? [{ label: po.salesOrderNum, to: `/os/so?id=${po.salesOrderId}` }] : []),
      ]} />

      <div className="page-head">
        <div>
          <div className="page-title">{po.displayId} <Chip label={po.status} /></div>
          <div className="page-sub">
            {po.clientName} · PO <strong>{po.poNumber}</strong> · received {fmtDate(po.receivedAt)} via {po.channel}
            {po.duplicateOfId && <span style={{ color: 'var(--rd)' }}> · ⚠ possible duplicate</span>}
          </div>
        </div>
        <div className="os-rail">
          {approved && !po.salesOrderId && (
            <button className="btn primary" disabled={busy} onClick={() => run(() => createSalesOrderFromPO(po), (r) => nav(`/os/so?id=${r}`))}>
              ▸ Create Sales Order
            </button>
          )}
          {po.salesOrderId && <button className="btn primary" onClick={() => nav(`/os/so?id=${po.salesOrderId}`)}>Open {po.salesOrderNum}</button>}
          {canApprove && (
            <>
              <button className="btn primary" disabled={busy || blockers.length > 0} title={blockers.length ? 'Hard rules unresolved' : ''} onClick={() => run(() => approveCustomerPO(po))}>✓ Approve PO</button>
              <button className="btn warn" disabled={busy} onClick={() => setExOpen(true)}>⚠ Approve with exception…</button>
              <button className="btn" disabled={busy} onClick={() => setClarOpen(true)}>? Request clarification</button>
              <button className="btn" disabled={busy} onClick={() => setLinkOpen(true)}>{po.quoteId ? '↺ Re-link quote' : '⚭ Link quote'}</button>
              <button className="btn danger" disabled={busy} onClick={() => { const r = prompt('Rejection reason (required):'); if (r) run(() => rejectCustomerPO(po, r)); }}>✕ Reject</button>
            </>
          )}
          {po.status === 'WAITING_ON_CLIENT' && (
            <button className="btn primary" disabled={busy} onClick={() => { const a = prompt(`Client answer to: "${po.clarification?.question}"`); if (a) run(() => resolveClientClarification(po, a)); }}>✓ Record client answer</button>
          )}
        </div>
      </div>

      {err && <div className="os-err">{err}</div>}
      {po.status === 'WAITING_ON_CLIENT' && po.clarification && (
        <div className="os-note">⏳ Waiting on client since {fmtDate(po.clarification.askedAt)}: “{po.clarification.question}”</div>
      )}
      {po.exception && (
        <div className="os-note" style={{ borderColor: 'var(--or)' }}>
          ⚠ Authorized exception ({po.exception.category}) — {po.exception.reason} · impact: {po.exception.impact} · risk: {po.exception.riskNote}
          <span style={{ color: 'var(--tx3)' }}> · approved by {po.exception.approvedBy} {fmtDate(po.exception.approvedAt)}</span>
        </div>
      )}

      <div className="seg" style={{ marginBottom: 14 }}>
        <button className={tab === 'compare' ? 'on' : ''} onClick={() => setTab('compare')}>Quote comparison</button>
        <button className={tab === 'po' ? 'on' : ''} onClick={() => setTab('po')}>PO details</button>
        <button className={tab === 'activity' ? 'on' : ''} onClick={() => setTab('activity')}>Activity</button>
      </div>

      {tab === 'compare' && (
        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'start' }}>
          <div className="card os-card">
            <div className="os-sect" style={{ marginTop: 0 }}>
              {cmp ? <>Compared against <strong>{cmp.quoteNum}</strong> · {fmtDate(cmp.ranAt)} · {cmp.mismatches} mismatch(es)</> : 'No comparison yet — link an approved quote.'}
            </div>
            {cmp && (
              <div className="table-wrap"><table>
                <thead><tr><th>Field</th><th>Approved quote</th><th>Customer PO</th><th>Result</th></tr></thead>
                <tbody>
                  {cmp.rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ color: 'var(--tx2)' }}>{r.label}</td>
                      <td>{r.quote}</td>
                      <td>{r.po}</td>
                      <td><span style={{ color: statusColor(r.verdict === 'match' ? 'ready' : r.verdict === 'info' ? 'draft' : r.verdict === 'missing' ? 'waiting' : 'blocked'), fontWeight: 600 }}>
                        {r.verdict === 'match' ? '✓ Match' : r.verdict === 'mismatch' ? '✕ Mismatch' : r.verdict === 'missing' ? '◌ Needs mapping' : 'ⓘ'}
                      </span></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
          <div>
            <Blockers items={approved ? [] : blockers} />
            {approved && <div className="os-clear">✓ PO approved{po.exception ? ' (with authorized exception)' : ''}.{!po.salesOrderId ? ' Next: create the sales order.' : ''}</div>}
            <div className="card os-card" style={{ marginTop: 12 }}>
              <div className="os-sect" style={{ marginTop: 0 }}>Original PO</div>
              {po.fileUrl ? <a href={po.fileUrl} target="_blank" rel="noreferrer" className="btn sm">📄 Open PO file</a> : <div className="page-sub">No file attached.</div>}
              {po.rawText && <pre className="os-raw">{po.rawText}</pre>}
              {po.notes && <div className="page-sub" style={{ marginTop: 8 }}>Notes: {po.notes}</div>}
            </div>
          </div>
        </div>
      )}

      {tab === 'po' && (
        <div className="card os-card">
          <div className="os-kv-grid">
            <div><em>Client</em><span>{po.clientName || '—'}</span></div>
            <div><em>Client email</em><span>{po.clientEmail || '—'}</span></div>
            <div><em>PO number</em><span>{po.poNumber}</span></div>
            <div><em>Quote</em><span>{po.quoteNum || 'not linked'}</span></div>
            <div><em>Total</em><span>{po.total ? usd(po.total) : '—'}</span></div>
            <div><em>Requested date</em><span>{po.requestedDate || '—'}</span></div>
            <div><em>Ship-to</em><span>{po.shipTo || '—'}</span></div>
            <div><em>Payment terms</em><span>{po.paymentTerms || '—'}</span></div>
            <div><em>Channel</em><span>{po.channel}</span></div>
            <div><em>Reviewer</em><span>{po.reviewer || '—'}</span></div>
          </div>
          <div className="os-sect">Lines</div>
          <div className="table-wrap"><table>
            <thead><tr><th>#</th><th>Customer part</th><th>Description</th><th>Qty</th><th>Unit</th><th>Total</th><th>Mapping</th></tr></thead>
            <tbody>
              {po.lines.map((l, i) => (
                <tr key={l.id}>
                  <td>{i + 1}</td><td>{l.customerPart || '—'}</td><td>{l.description}</td>
                  <td>{l.qty.toLocaleString()}</td><td>${l.unitPrice.toFixed(4)}</td><td>{usd(l.total)}</td>
                  <td><Chip label={l.mapStatus} /></td>
                </tr>
              ))}
              {!po.lines.length && <tr><td colSpan={7}><div className="empty">No lines captured.</div></td></tr>}
            </tbody>
          </table></div>
        </div>
      )}

      {tab === 'activity' && <div className="card os-card"><Timeline entityId={po.id} /></div>}

      <ExceptionModal open={exOpen} onClose={() => setExOpen(false)} busy={busy}
        onSubmit={(ex) => run(() => approveCustomerPO(po, ex), () => setExOpen(false))} />
      <Modal open={clarOpen} title="Request client clarification" onClose={() => setClarOpen(false)}>
        <ClarForm busy={busy} onSubmit={(q) => run(() => requestClientClarification(po, q), () => setClarOpen(false))} />
      </Modal>
      <LinkQuoteModal open={linkOpen} onClose={() => setLinkOpen(false)} busy={busy}
        onPick={(qid) => run(async () => {
          const qd = await getOsDoc<Record<string, unknown>>('quotes', qid);
          if (!qd) throw new Error('Quote not found.');
          await matchPOToQuote(po.id, qd as unknown as QuoteDoc);
        }, () => setLinkOpen(false))} />
    </>
  );
}

function ClarForm({ busy, onSubmit }: { busy: boolean; onSubmit: (q: string) => void }) {
  const [q, setQ] = useState('');
  return (
    <>
      <input placeholder="What do we need from the client?" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="os-modal-actions"><button className="btn primary" disabled={busy || !q.trim()} onClick={() => onSubmit(q)}>Send to client log</button></div>
    </>
  );
}

const EX_CATS: POException['category'][] = ['pricing', 'quantity', 'date', 'shipto', 'terms', 'other'];

function ExceptionModal({ open, onClose, onSubmit, busy }: { open: boolean; onClose: () => void; onSubmit: (ex: POException) => void; busy: boolean }) {
  const [f, setF] = useState({ reason: '', category: 'pricing' as POException['category'], impact: '', riskNote: '' });
  const ok = f.reason.trim() && f.impact.trim() && f.riskNote.trim();
  return (
    <Modal open={open} title="Approve with authorized exception" onClose={onClose}>
      <div className="page-sub" style={{ marginBottom: 10 }}>An exception requires reason, category, impact and a risk note. It is recorded on the PO and in the audit trail with your identity.</div>
      <label className="os-lbl">Category<select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value as POException['category'] })}>
        {EX_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select></label>
      <label className="os-lbl">Reason *<input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Why is this deviation acceptable?" /></label>
      <label className="os-lbl">Impact *<input value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })} placeholder="Commercial / production impact" /></label>
      <label className="os-lbl">Risk note *<input value={f.riskNote} onChange={(e) => setF({ ...f, riskNote: e.target.value })} placeholder="What could go wrong + mitigation" /></label>
      <div className="os-modal-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn warn" disabled={!ok || busy} onClick={() => onSubmit({ ...f, approvedBy: '', approvedAt: '' })}>⚠ Approve with exception</button>
      </div>
    </Modal>
  );
}

function LinkQuoteModal({ open, onClose, onPick, busy }: { open: boolean; onClose: () => void; onPick: (quoteId: string) => void; busy: boolean }) {
  const [quotes, setQuotes] = useState<RecentQuote[]>([]);
  const [f, setF] = useState('');
  const load = async () => { if (!quotes.length) setQuotes(await loadRecentQuotes(200)); };
  const shown = useMemo(() => quotes.filter((x) => !f.trim() || `${x.quoteNum} ${x.customerName} ${x.sku}`.toLowerCase().includes(f.toLowerCase())).slice(0, 15), [quotes, f]);
  return (
    <Modal open={open} title="Link approved quote" onClose={onClose}>
      <input placeholder="Search quotes (click to load)…" value={f} onFocus={load} onChange={(e) => setF(e.target.value)} />
      <div className="os-quote-picks">
        {shown.map((x) => (
          <button key={x.id} className="os-pick" disabled={busy} onClick={() => onPick(x.id)}>
            <strong>{x.quoteNum}</strong> {x.customerName} <span style={{ color: 'var(--tx3)' }}>{x.status}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
